import React from 'react';
import { ListItemProps } from '../../../mdc/list/ListItem';
import { DialogActionButton } from '../../common/Dialog';
import IconButton from '../../../mdc/IconButton';
import { Case, Site, ImageStatus } from '../../../services/buildfire/rdb/cases';
import Typography from '../../common/Typography';
import styled from 'styled-components';
import CaseAttachment, {
  CaseAttachmentProps,
} from '../siteAttachment/CaseAttachment';
import SiteDialog from './SiteDialog';
import { useDialog } from '../../../context/DialogContext';
import { IonItem, IonLabel, IonNote, IonBadge, IonIcon } from '@ionic/react';
import { getMode } from '@ionic/core';
import { warningOutline } from 'ionicons/icons';
import useThumbnailUrl from '../../../hooks/useThumbnail';
import useObjectUrl from '../../../hooks/useObjectUrl';
import useRemotePreviewUrl from '../../../hooks/useRemotePreviewUrl';

interface SiteListItemProps extends Partial<ListItemProps> {
  case?: Case;
  site: Site;
  userId: string;
  onEdit?: (
    site: Site,
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  onDelete?: (
    site: Site,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  siteIndex: number;
  isEditable?: boolean;
  isDeleteable?: boolean;
  onClickBehavior?: 'edit' | 'show';
  disableEdit?: boolean;
}

const ThumbnailImage = styled.img<{ $isIOS: boolean }>`
  height: 64px;
  width: 64px;
  border-radius: ${(props) => (props.$isIOS ? '8px' : '12px')};
  object-fit: cover;
`;

const PreviewImage = styled<
  React.FC<
    CaseAttachmentProps & {
      $isIOS: boolean;
    }
  >
>(CaseAttachment)`
  height: 64px;
  width: 64px;
  object-fit: cover;
  ${(props) => props.$isIOS && 'vertical-align: middle;'}
`;

const ThumbnailPlaceholder = styled.div<{ $isIOS: boolean }>`
  height: 64px;
  width: 64px;
  border-radius: ${(props) => (props.$isIOS ? '8px' : '12px')};
  background: rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.2);
  font-size: 12px;
  font-weight: 600;
`;

const Item = styled(IonItem)`
  --background: transparent;
`;

const StatusBadge = styled(IonBadge)`
  margin-left: 8px;
  font-size: 11px;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  vertical-align: middle;
`;

// Without min-width, the nowrap description sets the item's min-content width and
// blows out the width of every ancestor sized to fit-content
const Label = styled(IonLabel)`
  min-width: 0;
`;

const SiteDescription = styled(Typography)`
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
`;

const SiteListItem: React.FC<SiteListItemProps> = ({
  case: c,
  site,
  userId,
  onEdit,
  onDelete,
  isEditable = false,
  isDeleteable = false,
  siteIndex,
  onClickBehavior = 'show',
  disableEdit,
  ...props
}) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const { openDialog } = useDialog();
  const firstImage = site.images?.[0];
  const imageCount = site.images?.length || 0;
  const filePreview = firstImage instanceof File ? firstImage : undefined;
  const isImageFile =
    filePreview && typeof filePreview.type === 'string'
      ? filePreview.type.startsWith('image/')
      : false;
  const { url: thumbnailUrl, status: thumbnailStatus } = useThumbnailUrl(
    isImageFile ? filePreview : undefined
  );
  const fallbackPreviewUrl = useObjectUrl(
    filePreview && (!isImageFile || thumbnailStatus === 'error')
      ? filePreview
      : undefined
  );

  const isImageStatus = (img: any): img is ImageStatus => {
    return typeof img === 'object' && img !== null && 'status' in img;
  };

  const remoteFilename = React.useMemo(() => {
    if (filePreview) {
      return undefined;
    }
    if (typeof firstImage === 'string') {
      return firstImage;
    }
    if (firstImage && typeof firstImage === 'object' && 'filename' in firstImage) {
      return firstImage.filename;
    }
    if (firstImage && isImageStatus(firstImage)) {
      return firstImage.filename;
    }
    return undefined;
  }, [filePreview, firstImage]);

  const {
    url: remoteThumbnailUrl,
    status: remoteThumbnailStatus,
  } = useRemotePreviewUrl(remoteFilename, userId);

  const previewImageUrl = React.useMemo(() => {
    if (filePreview) {
      return isImageFile
        ? thumbnailUrl || fallbackPreviewUrl
        : fallbackPreviewUrl;
    }
    return remoteThumbnailUrl || undefined;
  }, [
    filePreview,
    isImageFile,
    thumbnailUrl,
    fallbackPreviewUrl,
    remoteThumbnailUrl,
  ]);

  const fallbackFileId = React.useMemo(() => {
    if (filePreview) {
      return undefined;
    }
    if (typeof firstImage === 'string') {
      return firstImage;
    }
    if (firstImage && typeof firstImage === 'object' && 'filename' in firstImage) {
      return firstImage.filename || (firstImage as any).url;
    }
    if (firstImage && isImageStatus(firstImage)) {
      if (firstImage.status === 'uploaded' && firstImage.url) {
        return firstImage.url;
      }
      return firstImage.filename;
    }
    return undefined;
  }, [filePreview, firstImage]);

  const uploadStatus = React.useMemo(() => {
    const statuses = site.images.map((img) => {
      if (img instanceof File) return 'pending';
      if (typeof img === 'string') return 'uploaded';
      if (typeof img === 'object' && 'status' in img) return img.status;
      return 'unknown';
    });

    const failedCount = statuses.filter((s) => s === 'failed').length;
    const uploadingCount = statuses.filter((s) => s === 'uploading').length;
    const pendingCount = statuses.filter((s) => s === 'pending').length;

    if (failedCount > 0) {
      return { status: 'failed', count: failedCount };
    }
    if (uploadingCount > 0) {
      return { status: 'uploading', count: uploadingCount };
    }
    if (pendingCount > 0) {
      return { status: 'pending', count: pendingCount };
    }
    return { status: 'uploaded', count: statuses.length };
  }, [site.images]);

  const ios = getMode() === 'ios';
  const rightHandContent =
    isEditable || isDeleteable ? (
      <>
        {isEditable && (
          <IconButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(site as any, e);
            }}
            icon="edit"
            disabled={disableEdit}
          />
        )}
        {isDeleteable && (
          <IconButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openDialog(
                <div>
                  <Typography variant="body" fontSize="14px">
                    Delete this site?
                  </Typography>
                  <Typography
                    variant="body"
                    fontSize="12px"
                    style={{ marginTop: '8px', color: 'var(--mdc-theme-error)' }}
                  >
                    This action cannot be reversed.
                  </Typography>
                </div>,
                {
                  onClose: (action) =>
                    action === 'accept' ? onDelete?.(site as any, e) : null,
                  actions: (
                    <>
                      <DialogActionButton action="close" emphasis="low">
                        Cancel
                      </DialogActionButton>
                      <DialogActionButton action="accept">
                        Yes, Delete
                      </DialogActionButton>
                    </>
                  ),
                }
              );
            }}
            icon="delete"
          />
        )}
      </>
    ) : previewImageUrl ? (
      <ThumbnailImage
        $isIOS={ios}
        src={previewImageUrl}
        alt={`${site.location} preview`}
      />
    ) : filePreview || remoteThumbnailStatus === 'loading' ? (
      <ThumbnailPlaceholder $isIOS={ios}>…</ThumbnailPlaceholder>
    ) : fallbackFileId ? (
      <PreviewImage
        $isIOS={ios}
        userId={userId}
        fileId={fallbackFileId}
        videoProps={{
          controls: false,
        }}
        imageProps={{
          placeholderProps: {
            style: {
              width: '64px',
              height: '64px',
            },
          },
        }}
      />
    ) : null;

  return (
    <>
      <Item
        button
        onClick={() => {
          if (onClickBehavior === 'show') {
            setShowDialog(true);
          } else {
            onEdit?.(site as any);
          }
        }}
      >
        <Label>
          <Typography as="h2" fontSize="16px" lineHeight="1.2em">
            {site.location}
            <Typography variant="primary" as="span">
              {' '}
              ({imageCount} image
              {imageCount !== 1 ? 's' : ''})
            </Typography>
            {uploadStatus.status === 'failed' && (
              <StatusBadge color="danger">
                <IonIcon icon={warningOutline} style={{ fontSize: '14px' }} />
                {uploadStatus.count} failed
              </StatusBadge>
            )}
          </Typography>
          <SiteDescription as="p">{site.description}</SiteDescription>
        </Label>
        <IonNote slot="end">{rightHandContent}</IonNote>
      </Item>

      <SiteDialog
        case={c}
        site={site}
        siteIndex={siteIndex}
        userId={userId}
        isOpen={showDialog}
        onDidDismiss={() => setShowDialog(false)}
        showActionMenu={site.images.every((img) => {
          if (typeof img === 'string') return true;
          if (typeof img === 'object' && 'status' in img) {
            return img.status === 'uploaded';
          }
          return false;
        })}
      />
    </>
  );
};

export default SiteListItem;
